'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { POLL_ID } from './chartData';

const MONO = "'IBM Plex Mono', var(--fxdw-mono), monospace";

type Choice = 'a' | 'b' | null;
interface PollState { a: number; b: number; choice: Choice }

// Srpska množina: 1 glas, 2–4 glasa, ostalo glasova
function glasovaLabel(n: number): string {
  const d = n % 10, h = n % 100;
  if (d === 1 && h !== 11) return `${n} glas`;
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return `${n} glasa`;
  return `${n} glasova`;
}

const CARDS = {
  a: {
    color: '#0E8455',
    title: 'Rast ka 6.000+',
    reason: 'Korekcija ② drži 4.194 i impuls ③ kreće naviše.',
  },
  b: {
    color: '#C8362A',
    title: 'Pad ka 3.400 i niže',
    reason: 'Rast sa 3.944 je bio ②(C); pravac ostaje naniže.',
  },
} as const;

export default function Poll() {
  const [state, setState] = useState<PollState>({ a: 0, b: 0, choice: null });
  const [hover, setHover] = useState<Choice>(null);
  const [resetHover, setResetHover] = useState(false);
  // Poslednji odgovor servera pobeđuje; guard protiv out-of-order fetch-eva
  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    try {
      const res = await fetch(`/api/poll/${POLL_ID}`, { cache: 'no-store' });
      if (!res.ok) return;
      const d = await res.json();
      if (seq === reqSeq.current) setState({ a: d.a ?? 0, b: d.b ?? 0, choice: d.choice ?? null });
    } catch { /* zadrži poslednje poznato stanje */ }
  }, []);

  // Inicijalno + osvežavanje na ~30s i pri fokusu taba (README: State Management)
  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [load]);

  async function vote(next: Choice) {
    const prev = state;
    if (next === prev.choice) return;
    // Optimistički UI: dekrement stare opcije, inkrement nove
    setState({
      a: prev.a + (next === 'a' ? 1 : 0) - (prev.choice === 'a' ? 1 : 0),
      b: prev.b + (next === 'b' ? 1 : 0) - (prev.choice === 'b' ? 1 : 0),
      choice: next,
    });
    const seq = ++reqSeq.current;
    try {
      const res = await fetch(`/api/poll/${POLL_ID}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ choice: next, source: 'page' }),
      });
      if (!res.ok) { load(); return; }
      const d = await res.json();
      if (seq === reqSeq.current) setState({ a: d.a ?? 0, b: d.b ?? 0, choice: d.choice ?? null });
    } catch {
      load();
    }
  }

  const tot = state.a + state.b;
  const pct = (v: number) => (tot ? Math.round((v / tot) * 100) + '%' : '—');
  const width = (v: number) => (tot ? ((v / tot) * 100).toFixed(1) + '%' : '0%');

  return (
    <div style={{ border: '1px solid #E4E8EE', borderRadius: 18, padding: 'clamp(18px, 1.8vw, 34px) clamp(16px, 2.1vw, 40px)', background: '#FAFBFC' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '6px 16px' }}>
        <div style={{ fontSize: 'clamp(20px, 1.5vw, 28px)', fontWeight: 800, color: '#0E2A3D' }}>
          Anketa — koji scenario vidite kao verovatniji?
        </div>
        <div style={{ fontFamily: MONO, fontSize: 15, color: '#7C8794' }}>{glasovaLabel(tot)}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 'clamp(14px, 1.2vw, 22px)', marginTop: 26 }}>
        {(['a', 'b'] as const).map((key) => {
          const c = CARDS[key];
          const active = state.choice === key || hover === key;
          return (
            <div
              key={key}
              onClick={() => vote(key)}
              onMouseEnter={() => setHover(key)}
              onMouseLeave={() => setHover(null)}
              style={{
                cursor: 'pointer', borderRadius: 14, padding: 'clamp(16px, 1.3vw, 24px)', background: '#FFFFFF',
                border: `2px solid ${active ? c.color : '#E4E8EE'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 30, height: 30, borderRadius: 9, background: c.color, color: '#fff', fontSize: 15, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {key.toUpperCase()}
                </div>
                <div style={{ fontSize: 'clamp(16px, 1.05vw, 20px)', fontWeight: 700, color: '#0E2A3D', minWidth: 0 }}>{c.title}</div>
                <div style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 22, fontWeight: 600, color: c.color }}>
                  {pct(state[key])}
                </div>
              </div>
              <div style={{ height: 10, borderRadius: 5, background: '#E9EDF2', marginTop: 18, overflow: 'hidden' }}>
                <div style={{ height: 10, borderRadius: 5, background: c.color, transition: 'width .35s ease', width: width(state[key]) }} />
              </div>
              <div style={{ fontSize: 15, color: '#5C6B78', marginTop: 14 }}>{c.reason}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 24 }}>
        <div style={{ fontSize: 15, color: '#7C8794' }}>
          {state.choice
            ? `Vaš glas: scenario ${state.choice.toUpperCase()}. Kliknite drugu karticu da promenite.`
            : 'Kliknite karticu da glasate.'}
        </div>
        <div
          onClick={() => state.choice && vote(null)}
          onMouseEnter={() => setResetHover(true)}
          onMouseLeave={() => setResetHover(false)}
          style={{
            cursor: 'pointer', fontSize: 14, fontWeight: 600,
            color: resetHover ? '#0E2A3D' : '#98A1B0',
            border: `1px solid ${resetHover ? '#C6CDD8' : '#E4E8EE'}`,
            borderRadius: 8, padding: '8px 14px',
          }}
        >
          Poništi glas
        </div>
      </div>
    </div>
  );
}
